import { useEffect, useState } from "react";

import { useStateContext } from "@/context/context-provider";
import { useSocketContext } from "@/context/socket-context-provider";

type TimerProps = {
    paused: boolean;
};
const Timer = ({ paused }: TimerProps) => {
    const { roomUrl, hideTimer } = useStateContext();
    const { lobbyTimer, lobbyId } = useSocketContext();
    const [secondsElapsed, setSecondsElapsed] = useState(0);

    const isMultiplayer = Boolean(lobbyId);

    /******************************** MULTIPLAYER LOGIK ********************************/
    useEffect(() => {
        if (!isMultiplayer) {
            return;
        }

        const startTime = lobbyTimer?.startTime;
        const endTime = lobbyTimer?.endTime;

        if (!startTime) {
            return;
        }

        const updateTimer = () => {
            const now = endTime || Date.now();
            setSecondsElapsed(Math.max(0, Math.floor((now - startTime) / 1000)));
        };

        const timeoutId = setTimeout(updateTimer, 0);

        if (endTime) {
            return () => clearTimeout(timeoutId);
        }

        const intervalId = setInterval(updateTimer, 1000);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
        };
    }, [isMultiplayer, lobbyTimer?.startTime, lobbyTimer?.endTime]);

    /******************************** SINGLEPLAYER LOGIK ********************************/
    useEffect(() => {
        if (isMultiplayer || !roomUrl) {
            return;
        }

        const getStoredData = () => {
            try {
                return JSON.parse(localStorage.getItem(roomUrl) || "{}");
            } catch {
                return {};
            }
        };

        const saveStoredData = (data) => {
            const current = getStoredData();
            localStorage.setItem(roomUrl, JSON.stringify({ ...current, ...data }));
        };

        const data = getStoredData();
        let accumulated = data.elapsedTime || 0;
        let startTime = data.startTime || null;

        if (paused) {
            if (startTime) {
                accumulated += Math.floor((Date.now() - startTime) / 1000);
                saveStoredData({ elapsedTime: accumulated, startTime: null });
            }
        } else if (!startTime) {
            startTime = Date.now();
            saveStoredData({ startTime });
        }

        const updateTimer = () => {
            const currentData = getStoredData();
            const acc = currentData.elapsedTime || 0;
            const start = currentData.startTime;

            if (!start || paused) {
                setSecondsElapsed(acc);
            } else {
                const diffInSeconds = Math.floor((Date.now() - start) / 1000);
                setSecondsElapsed(acc + diffInSeconds);
            }
        };

        const timeoutId = setTimeout(updateTimer, 0);

        if (paused) {
            return () => clearTimeout(timeoutId);
        }

        const intervalId = setInterval(updateTimer, 1000);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
            const currentData = getStoredData();
            if (currentData.startTime) {
                const activeDiff = Math.floor((Date.now() - currentData.startTime) / 1000);
                saveStoredData({
                    elapsedTime: (currentData.elapsedTime || 0) + activeDiff,
                    startTime: null,
                });
            }
        };
    }, [isMultiplayer, paused, roomUrl]);

    if (hideTimer) {
        return null;
    }

    const hours = Math.floor(secondsElapsed / 3600);
    const minutes = Math.floor((secondsElapsed % 3600) / 60);
    const seconds = secondsElapsed % 60;

    return (
        <h2 className="timer">
            {hours > 0 && `${String(hours).padStart(2, "0")}:`}
            {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </h2>
    );
};

export default Timer;
